import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useItensChecklistListaPagination } from "../hooks/Itens_Checklist_lista/pagination";
import { useNavigate, useParams } from "react-router";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { ItensChecklistListaForm, ItensChecklistListaSchema } from "../schemas/ChecklistItens_cadastro/Checklist_lista_Itens";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { ItemChecklistLista } from "../../domain/models/ItemChecklistLista";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

export const useItensChecklistViewModel = () => {
  const {
    commons: {getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    projetos: {
      getAllItensChecklistUseCase,
      getItensChecklistByIdUseCase,
      createItensChecklistUseCase,
      updateItensChecklistUseCase,
      deleteItensChecklistUseCase,
    },
  } = useDependencyInjection();
  const navigate = useNavigate();
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const hookForm = useForm<ItensChecklistListaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ItensChecklistListaSchema),
  });
    

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();
  const pagination = useItensChecklistListaPagination();
  const [itemChecklistLista, setitemChecklistLista] = useState<ItemChecklistLista[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [selectedItemChecklist, setSelectedItemChecklist] = useState<ItemChecklistLista | undefined> (undefined);
  const [itemChecklistModal, setItemChecklistModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [idChecklist, setIdChecklist] = useState<number>();
  const { id } = useParams();

  type LoadProjetoFuncState = {
    idChecklist?: number;
    Page?: number | null;
    RowsPerPage?: number | null;
  };


  //#region load

  const loadItemChecklis = useCallback(
    async (funcState?: LoadProjetoFuncState) => {
      try {
        const filterid = funcState?.idChecklist ?? idChecklist;
        const filterpage = funcState?.Page ?? pagination.page;
        const filterRowspage = funcState?.RowsPerPage ?? pagination.rowsPerPage;
        if(filterid){
          await getAllItensChecklistUseCase.getAllItensChecklist(
            filterpage,
            filterRowspage,
            filterid,
          );
          setitemChecklistLista(getAllItensChecklistUseCase.items);
          pagination.setRowsPerPage(getAllItensChecklistUseCase.pageSize);
          pagination.setTotalItems(getAllItensChecklistUseCase.totalItems);
        }
      } catch (e: unknown) {
        setitemChecklistLista([]);
        throw new Error("Falha ao buscar Item Checklist!");
      }
    },
    [idChecklist, pagination, getAllItensChecklistUseCase]
  );

  //#endregion

  //#region useEffect
  usePageStartBootstrap(async () => {
    try {
      if(id){
        await loadItemChecklis({idChecklist: Number(id)});
        setIdChecklist(Number(id));
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das paginas!")[0]);
    }
  });
  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
     if(id){
        await loadItemChecklis({idChecklist: Number(id)});
        setIdChecklist(Number(id));
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [id, loadItemChecklis, pushErrorNotification]);

  //#endregion
  
  //#region Evento
  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useItensChecklistListaPagination.setState({ page: page + 1 });
        await loadItemChecklis({ Page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [loadItemChecklis, pushErrorNotification],
  );
  
  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
        await loadItemChecklis({ Page: 1, RowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [loadItemChecklis, pagination, pushErrorNotification],
  );

  const handleModalOpenState = useCallback((id: number | null, openState: boolean) => {
    if (openState) {
      if (idChecklist && itemChecklistLista) {
        setSelectedItemChecklist(itemChecklistLista.find(x => x.IdItemChecklist === id));
      } else {
        setSelectedItemChecklist(undefined);
      }
    }
    setItemChecklistModal(openState);
  }, [idChecklist, itemChecklistLista]);


  const handleCheckListSubmit = useCallback(
    async (form: ItensChecklistListaForm) => {
      try {
        setSaving(true);
        if(idChecklist){
            if (selectedItemChecklist) {
              await updateItensChecklistUseCase.updateItensChecklist(selectedItemChecklist.IdItemChecklist, selectedItemChecklist.IdChecklist, form.codigo, form.descricao);
    
              pushInfoNotification("SUCESSO", "Item CheckLis atualizado com sucesso!!", { isAutoFinish: true })
            } else {
              await createItensChecklistUseCase.createItensChecklist(idChecklist, form.codigo, form.descricao);
    
              pushInfoNotification("SUCESSO", "Item CheckLis criado com sucesso!!", { isAutoFinish: true })
            }
        }
        await loadItemChecklis({idChecklist: idChecklist});
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar Checklist!"));
      } finally {
        setSaving(false);
        setItemChecklistModal(false);
      }
    },
    [selectedItemChecklist, loadItemChecklis, idChecklist, updateItensChecklistUseCase, pushInfoNotification, createItensChecklistUseCase, pushErrorNotification],
  );
  const DeletarItemCheckList = useCallback(async () => {
    if (selectedItemChecklist) {
      try {
          setSaving(true);
          await deleteItensChecklistUseCase.deleteItensChecklist(selectedItemChecklist.IdItemChecklist);
          await loadItemChecklis({idChecklist: idChecklist});
      } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
      }
    }
  }, [deleteItensChecklistUseCase, idChecklist, loadItemChecklis, pushErrorNotification, selectedItemChecklist]);
  

  const DeleteOpenModal = useCallback((id: number | null, openState: boolean) => {
    if (openState) {
      if (id && itemChecklistLista) {
        setSelectedItemChecklist(itemChecklistLista.find(x => x.IdItemChecklist === id));
      } else {
        setSelectedItemChecklist(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [itemChecklistLista]);
  
  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } 
    },
       [ changeHistory],
  );
  const handleVoltar = useCallback(() => {
    navigate(`/Checklist_lista/`)
      
  }, [navigate]);
    

  //#endregion


  return {
    pagination,
    errorMessage,
    loading,
    itemChecklistModal,
    saving,
    openDeleteModal,
    selectedItemChecklist,
    itemChecklistLista,
    hookForm,
    changeHistory,
    setItemChecklistModal,
    handleReload,
    handleModalOpenState,
    DeleteOpenModal,
    handleRowsPerPageChange,
    handlePageChange,
    handleCheckListSubmit,
    DeletarItemCheckList,
    handleOpenChangeHistory,
    handleVoltar,
  };
};
